import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, LogOut, AlertCircle, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const TransportAddStudent = () => {
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isDiscontinueModalOpen, setIsDiscontinueModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [discontinueData, setDiscontinueData] = useState(null);
  const [discontinueDate, setDiscontinueDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    student_id: '', student_name: '', route_id: '', route_name: '', 
    pickup_point: '', drop_point: '', 
    admission_date: new Date().toISOString().split('T')[0], 
    monthly_fee: 0, status: 'Active', remarks: '' 
  });

  useEffect(() => { 
    fetchTransportStudents(); 
    fetchRoutes(); 
    fetchAllStudents(); 
    fetchPayments();
  }, []);

  const fetchTransportStudents = async () => { 
    try { 
      const response = await apiClient.get('/api/transport/students'); 
      setStudents(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
      setStudents([]);
    } 
  };

  const fetchRoutes = async () => { 
    try { 
      const response = await apiClient.get('/api/transport/routes'); 
      setRoutes(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
      setRoutes([]);
    } 
  };

  const fetchAllStudents = async () => { 
    try { 
      const response = await apiClient.get('/api/students/dropdown'); 
      setAllStudents(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
      setAllStudents([]);
    } 
  };

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/transport/fee-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
      setPayments([]);
    }
  };

  const handleStudentChange = (studentId) => { 
    const student = allStudents.find(s => s.id === studentId); 
    if (student) setFormData({ ...formData, student_id: studentId, student_name: student.full_name }); 
  };

  const handleRouteChange = (routeId) => { 
    const route = routes.find(r => r.id === routeId); 
    if (route) setFormData({ ...formData, route_id: routeId, route_name: route.route_name, monthly_fee: route.monthly_fee }); 
  };

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setLoading(true); 
    try { 
      if (isEditMode && editingId) {
        await apiClient.put(`/api/transport/students/${editingId}`, {
          route_id: formData.route_id,
          route_name: formData.route_name,
          pickup_point: formData.pickup_point,
          drop_point: formData.drop_point,
          admission_date: formData.admission_date,
          monthly_fee: formData.monthly_fee,
          remarks: formData.remarks,
        });
        alert('Transport student updated!');
      } else {
        const response = await apiClient.post('/api/transport/students', formData);
        if (response.status === 200 || response.data) {
          alert('Student added to transport!');
        }
      }
      fetchTransportStudents();
      handleCloseModal();
    } catch (error) { 
      alert(isEditMode ? 'Failed to update' : 'Failed'); 
    } finally { 
      setLoading(false); 
    } 
  };

  const handleEditClick = (student) => {
    setIsEditMode(true);
    setEditingId(student.id);
    setFormData({
      student_id: student.student_id || '',
      student_name: student.student_name || '',
      route_id: student.route_id || '',
      route_name: student.route_name || '',
      pickup_point: student.pickup_point || '',
      drop_point: student.drop_point || '',
      admission_date: student.admission_date || new Date().toISOString().split('T')[0],
      monthly_fee: student.monthly_fee || 0,
      status: student.status || 'Active',
      remarks: student.remarks || '',
    });
    setIsModalOpen(true);
  };

  // Calculate dues for a student up to discontinue date
  const calculateDues = (student, upToDate = new Date()) => {
    const admissionDate = new Date(student.admission_date);
    const endDate = new Date(upToDate);
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();
    
    // Get paid months for this student
    const studentPayments = payments.filter(p => 
      p.student_id === student.student_id || p.payer_name === student.student_name
    );
    const paidMonths = studentPayments.map(p => `${p.month}-${p.year}`);
    const totalPaid = studentPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    
    // Calculate due months only up to discontinue date
    const dueMonthsList = [];
    let startYear = admissionDate.getFullYear();
    let startMonth = admissionDate.getMonth();
    
    for (let year = startYear; year <= endYear; year++) {
      const monthStart = (year === startYear) ? startMonth : 0;
      const monthEnd = (year === endYear) ? endMonth : 11;
      
      for (let month = monthStart; month <= monthEnd; month++) {
        const monthName = MONTHS[month];
        const key = `${monthName}-${year}`;
        
        if (!paidMonths.includes(key)) {
          dueMonthsList.push({ month: monthName, year, key });
        }
      }
    }
    
    const feeDue = dueMonthsList.length * (student.monthly_fee || 0);
    
    return {
      dueMonths: dueMonthsList,
      dueMonthsCount: dueMonthsList.length,
      feeDue: feeDue,
      totalPaid: totalPaid,
      canDiscontinue: dueMonthsList.length === 0
    };
  };

  const handleDiscontinueClick = (student) => {
    setSelectedStudent(student);
    const today = new Date().toISOString().split('T')[0];
    setDiscontinueDate(today);
    const dues = calculateDues(student, new Date(today));
    setDiscontinueData(dues);
    setIsDiscontinueModalOpen(true);
  };

  const handleDiscontinueDateChange = (newDate) => {
    setDiscontinueDate(newDate);
    if (selectedStudent) {
      const dues = calculateDues(selectedStudent, new Date(newDate));
      setDiscontinueData(dues);
    }
  };

  const handleDiscontinue = async () => {
    if (!selectedStudent) return;
    
    if (!discontinueData.canDiscontinue) {
      alert('Cannot discontinue! Please clear all dues first via Fee Payment.');
      return;
    }
    
    setLoading(true);
    try {
      await apiClient.put(`/api/transport/students/${selectedStudent.id}/discontinue?end_date=${discontinueDate}`);
      alert('Student discontinued successfully!');
      fetchTransportStudents();
      setIsDiscontinueModalOpen(false);
      setSelectedStudent(null);
      setDiscontinueData(null);
      setDiscontinueDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      alert('Failed to discontinue student');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => { 
    if (window.confirm('Delete this student from transport?')) { 
      await apiClient.delete(`/api/transport/students/${id}`); 
      fetchTransportStudents(); 
    } 
  };

  const handleCloseModal = () => { 
    setIsModalOpen(false); 
    setIsEditMode(false);
    setEditingId(null);
    setFormData({ 
      student_id: '', student_name: '', route_id: '', route_name: '', 
      pickup_point: '', drop_point: '', 
      admission_date: new Date().toISOString().split('T')[0], 
      monthly_fee: 0, status: 'Active', remarks: '' 
    }); 
  };

  // Filter students not already in transport
  const availableStudents = allStudents.filter(
    s => !students.some(ts => ts.student_id === s.id || ts.student_name === s.full_name)
  );

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Transport Students</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Add Student
          </Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Student Name</th>
                <th className="text-left p-3">Route</th>
                <th className="text-left p-3">Pickup</th>
                <th className="text-left p-3">Drop</th>
                <th className="text-left p-3">Admission</th>
                <th className="text-right p-3">Fee/Month</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-4 text-gray-500">No students in transport.</td></tr>
              ) : (
                students.map((student) => {
                  const dues = calculateDues(student);
                  return (
                    <tr key={student.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{student.student_name}</td>
                      <td className="p-3">{student.route_name}</td>
                      <td className="p-3">{student.pickup_point}</td>
                      <td className="p-3">{student.drop_point}</td>
                      <td className="p-3">{formatDate(student.admission_date)}</td>
                      <td className="p-3 text-right">₹{student.monthly_fee}</td>
                      <td className="p-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${student.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {student.status}
                          </span>
                          {dues.feeDue > 0 && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 whitespace-nowrap">
                              Due: ₹{dues.feeDue}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex gap-2 justify-center">
                          {student.status === 'Active' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleEditClick(student)} data-testid={`transport-edit-btn-${student.id}`} title="Edit">
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDiscontinueClick(student)} title="Discontinue" data-testid={`transport-discontinue-btn-${student.id}`}>
                                <LogOut className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(student.id)} data-testid={`transport-delete-btn-${student.id}`} title="Delete">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add / Edit Student Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{isEditMode ? 'Edit Transport Student' : 'Add Student to Transport'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isEditMode && (
              <div>
                <Label>Select Student *</Label>
                <Select value={formData.student_id} onValueChange={handleStudentChange}>
                  <SelectTrigger><SelectValue placeholder="-- Select Student from Database --" /></SelectTrigger>
                  <SelectContent>
                    {availableStudents.length === 0 ? (
                      <SelectItem value="__none__" disabled>No students available</SelectItem>
                    ) : (
                      availableStudents.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.full_name} ({s.admission_number || 'N/A'})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.student_name && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p><strong>Selected:</strong> {formData.student_name}</p>
              </div>
            )}

            <div>
              <Label>Route *</Label>
              <Select value={formData.route_id} onValueChange={handleRouteChange}>
                <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent>
                  {routes.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.route_name} - ₹{r.monthly_fee}/month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pickup Point *</Label>
                <Input required value={formData.pickup_point} onChange={(e) => setFormData({ ...formData, pickup_point: e.target.value })} />
              </div>
              <div>
                <Label>Drop Point *</Label>
                <Input required value={formData.drop_point} onChange={(e) => setFormData({ ...formData, drop_point: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Admission Date *</Label>
                <Input type="date" required value={formData.admission_date} onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })} />
                <p className="text-xs text-gray-500 mt-1">Fee calculation starts from this date</p>
              </div>
              <div>
                <Label>Monthly Fee (₹) *</Label>
                <Input 
                  type="number" 
                  required 
                  value={formData.monthly_fee} 
                  onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) || 0 })} 
                  className={formData.route_id && formData.monthly_fee > 0 ? 'bg-green-50' : ''}
                />
                {formData.route_id && formData.monthly_fee > 0 && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from route settings</p>
                )}
              </div>
            </div>

            <div>
              <Label>Remarks</Label>
              <Input value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Optional remarks" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading || !formData.student_name || !formData.route_id}>
                {loading ? 'Saving...' : (isEditMode ? 'Update Student' : 'Add Student')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discontinue Modal */}
      <Dialog open={isDiscontinueModalOpen} onOpenChange={setIsDiscontinueModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Discontinue Student from Transport</DialogTitle>
          </DialogHeader>
          
          {selectedStudent && discontinueData && (
            <div className="space-y-3">
              {/* Student Info - Compact */}
              <div className="bg-gray-50 p-2 rounded text-sm grid grid-cols-2 gap-1">
                <span><strong>Student:</strong> {selectedStudent.student_name}</span>
                <span><strong>Route:</strong> {selectedStudent.route_name}</span>
                <span><strong>Admission:</strong> {formatDate(selectedStudent.admission_date)}</span>
                <span><strong>Fee:</strong> ₹{selectedStudent.monthly_fee}/month</span>
              </div>

              {/* Discontinue Date */}
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Discontinue Date:</Label>
                <Input 
                  type="date" 
                  value={discontinueDate} 
                  onChange={(e) => handleDiscontinueDateChange(e.target.value)}
                  min={selectedStudent.admission_date}
                  className="flex-1"
                />
              </div>

              {/* Dues Summary - Compact */}
              <div className="text-sm space-y-1">
                <div className="flex justify-between p-1.5 bg-green-100 rounded">
                  <span>Total Paid</span>
                  <span className="text-green-600 font-bold">₹{discontinueData.totalPaid}</span>
                </div>
                <div className="flex justify-between p-1.5 bg-gray-100 rounded">
                  <span>Fee Due ({discontinueData.dueMonthsCount} months)</span>
                  <span className={discontinueData.feeDue > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>₹{discontinueData.feeDue}</span>
                </div>
              </div>

              {/* Due Months - Compact scrollable */}
              {discontinueData.dueMonths.length > 0 && (
                <div className="bg-red-50 p-2 rounded border border-red-200 max-h-20 overflow-y-auto">
                  <p className="text-red-700 text-xs font-medium mb-1">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Due: {discontinueData.dueMonths.map(d => `${d.month.slice(0,3)} ${d.year}`).join(', ')}
                  </p>
                </div>
              )}

              {/* Status Message */}
              {discontinueData.canDiscontinue ? (
                <p className="text-green-700 text-sm bg-green-50 p-2 rounded">✅ All dues cleared! Ready to discontinue.</p>
              ) : (
                <p className="text-red-700 text-sm bg-red-50 p-2 rounded">
                  ❌ Clear ₹{discontinueData.feeDue} dues first via Fee Payment
                </p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDiscontinueModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleDiscontinue} 
                  disabled={loading || !discontinueData.canDiscontinue}
                  className={discontinueData.canDiscontinue ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {loading ? 'Processing...' : 'Confirm Discontinue'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportAddStudent;
